import React, { useState, useEffect } from 'react';
import { Card, Button, Input, Label } from '../ui';
import { useAuth } from '../../contexts/AuthContext';
import {
  getAllCreditRequests,
  getAllMessagingCredits,
  getAllCreditPackages,
  getAllOrganizations,
  fulfillCreditRequest,
  rejectCreditRequest,
  manualTopUp,
  createCreditPackage,
  updateCreditPackage,
  getAllActivationRequests,
  approveActivationRequest,
  rejectActivationRequest
} from '../../services/creditService';
import { CreditRequest, MessagingCredits, CreditPackage, Organization, MessagingActivationRequest } from '../../types';

type TabKey = 'activation' | 'requests' | 'credits' | 'topup' | 'packages';

const CreditManagement: React.FC = () => {
  const { currentUser, userData } = useAuth();
  const [activeTab, setActiveTab] = useState<TabKey>('activation');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  // Requests state
  const [requests, setRequests] = useState<CreditRequest[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(true);

  // Credits overview state
  const [orgCredits, setOrgCredits] = useState<MessagingCredits[]>([]);
  const [creditsLoading, setCreditsLoading] = useState(true);

  // Top-up state
  const [topUpOrgId, setTopUpOrgId] = useState('');
  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpLoading, setTopUpLoading] = useState(false);

  // Organizations state
  const [organizations, setOrganizations] = useState<Organization[]>([]);

  // Packages state
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [showNewPackage, setShowNewPackage] = useState(false);
  const [newPkg, setNewPkg] = useState({ name: '', creditAmount: '', price: '', currency: 'USD', order: '0' });

  // Activation requests state
  const [activationRequests, setActivationRequests] = useState<MessagingActivationRequest[]>([]);
  const [activationLoading, setActivationLoading] = useState(true);
  const [activatingId, setActivatingId] = useState<string | null>(null);

  // Reject dialog state (shared for credit requests and activation requests)
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);
  const [rejectTargetType, setRejectTargetType] = useState<'credit' | 'activation'>('credit');
  const [rejectReason, setRejectReason] = useState('');

  const showToast = (message: string, type: 'success' | 'error' | 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Build org name lookup map
  const orgMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    organizations.forEach(org => { map[org.id] = org.name; });
    return map;
  }, [organizations]);

  const getOrgName = (orgId: string) => orgMap[orgId] || orgId;

  // Load data
  useEffect(() => {
    loadOrganizations();
    loadActivationRequests();
    loadRequests();
    loadCredits();
    loadPackages();
  }, []);

  const loadOrganizations = async () => {
    try {
      const data = await getAllOrganizations();
      setOrganizations(data);
    } catch (error) {
      console.error('Error loading organizations:', error);
    }
  };

  const loadActivationRequests = async () => {
    setActivationLoading(true);
    try {
      const data = await getAllActivationRequests();
      setActivationRequests(data);
    } catch (error) {
      console.error('Error loading activation requests:', error);
    } finally {
      setActivationLoading(false);
    }
  };

  const handleActivate = async (req: MessagingActivationRequest) => {
    if (!currentUser) return;
    setActivatingId(req.id);
    try {
      await approveActivationRequest(
        req.id,
        req.organizationId,
        req.organizationName,
        { userId: currentUser.uid, name: userData?.name || 'Admin' }
      );
      showToast('Organization messaging activated successfully', 'success');
      loadActivationRequests();
    } catch (error: any) {
      showToast(error.message || 'Failed to activate messaging', 'error');
    } finally {
      setActivatingId(null);
    }
  };

  const loadRequests = async () => {
    setRequestsLoading(true);
    try {
      const data = await getAllCreditRequests();
      setRequests(data);
    } catch (error) {
      console.error('Error loading requests:', error);
    } finally {
      setRequestsLoading(false);
    }
  };

  const loadCredits = async () => {
    setCreditsLoading(true);
    try {
      const data = await getAllMessagingCredits();
      setOrgCredits(data);
    } catch (error) {
      console.error('Error loading credits:', error);
    } finally {
      setCreditsLoading(false);
    }
  };

  const loadPackages = async () => {
    setPackagesLoading(true);
    try {
      const data = await getAllCreditPackages();
      setPackages(data);
    } catch (error) {
      console.error('Error loading packages:', error);
    } finally {
      setPackagesLoading(false);
    }
  };

  const handleFulfill = async (requestId: string) => {
    if (!currentUser) return;
    try {
      await fulfillCreditRequest(requestId, {
        userId: currentUser.uid,
        name: userData?.name || 'Admin'
      });
      showToast('Credit request fulfilled successfully', 'success');
      loadRequests();
      loadCredits();
    } catch (error: any) {
      showToast(error.message || 'Failed to fulfill request', 'error');
    }
  };

  const handleReject = async () => {
    if (!rejectTarget || !currentUser) return;
    try {
      const reviewedBy = { userId: currentUser.uid, name: userData?.name || 'Admin' };
      if (rejectTargetType === 'activation') {
        await rejectActivationRequest(rejectTarget, rejectReason, reviewedBy);
        showToast('Activation request rejected', 'info');
        loadActivationRequests();
      } else {
        await rejectCreditRequest(rejectTarget, rejectReason, reviewedBy);
        showToast('Credit request rejected', 'info');
        loadRequests();
      }
      setRejectTarget(null);
      setRejectTargetType('credit');
      setRejectReason('');
    } catch (error: any) {
      showToast(error.message || 'Failed to reject request', 'error');
    }
  };

  const handleTopUp = async () => {
    if (!topUpOrgId || !topUpAmount || !currentUser) return;
    const amount = parseInt(topUpAmount);
    if (isNaN(amount) || amount <= 0) {
      showToast('Enter a valid amount', 'error');
      return;
    }

    setTopUpLoading(true);
    try {
      await manualTopUp(topUpOrgId, amount, {
        userId: currentUser.uid,
        name: userData?.name || 'Admin'
      });
      showToast(`Added ${amount} credits to organization`, 'success');
      setTopUpOrgId('');
      setTopUpAmount('');
      loadCredits();
    } catch (error: any) {
      showToast(error.message || 'Failed to top up', 'error');
    } finally {
      setTopUpLoading(false);
    }
  };

  const handleCreatePackage = async () => {
    const creditAmount = parseInt(newPkg.creditAmount);
    const price = parseFloat(newPkg.price);
    const order = parseInt(newPkg.order);

    if (!newPkg.name || isNaN(creditAmount) || isNaN(price) || creditAmount <= 0 || price <= 0) {
      showToast('Fill in all fields with valid values', 'error');
      return;
    }

    try {
      await createCreditPackage({
        name: newPkg.name,
        creditAmount,
        price,
        currency: newPkg.currency,
        isActive: true,
        order: isNaN(order) ? 0 : order
      });
      showToast('Package created', 'success');
      setShowNewPackage(false);
      setNewPkg({ name: '', creditAmount: '', price: '', currency: 'USD', order: '0' });
      loadPackages();
    } catch (error: any) {
      showToast(error.message || 'Failed to create package', 'error');
    }
  };

  const handleTogglePackage = async (pkg: CreditPackage) => {
    try {
      await updateCreditPackage(pkg.id, { isActive: !pkg.isActive });
      loadPackages();
    } catch (error: any) {
      showToast(error.message || 'Failed to update package', 'error');
    }
  };

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'activation', label: 'Activation Requests' },
    { key: 'requests', label: 'Credit Requests' },
    { key: 'credits', label: 'Organization Credits' },
    { key: 'topup', label: 'Manual Top-Up' },
    { key: 'packages', label: 'Packages' }
  ];

  const getStatusBadge = (status: string) => {
    const classes: Record<string, string> = {
      pending: 'bg-yellow-50 text-yellow-700',
      approved: 'bg-blue-50 text-blue-700',
      fulfilled: 'bg-green-50 text-green-700',
      rejected: 'bg-red-50 text-red-700'
    };
    return classes[status] || 'bg-secondary-50 text-secondary-700';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">Credit Management</h1>
        <p className="text-secondary-600">Manage messaging credits for all organizations</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-secondary-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === tab.key
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'activation' && (
        <Card>
          <div className="p-4 border-b border-secondary-200 flex items-center justify-between">
            <h3 className="font-semibold text-secondary-900">Messaging Activation Requests</h3>
            <Button size="sm" variant="outline" onClick={loadActivationRequests}>
              Refresh
            </Button>
          </div>
          {activationLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : activationRequests.length === 0 ? (
            <div className="p-6 text-center text-secondary-500 text-sm">No activation requests yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-secondary-200 bg-secondary-50">
                    <th className="text-left px-4 py-2 font-medium text-secondary-600">Organization</th>
                    <th className="text-left px-4 py-2 font-medium text-secondary-600">Requested By</th>
                    <th className="text-left px-4 py-2 font-medium text-secondary-600">Date</th>
                    <th className="text-left px-4 py-2 font-medium text-secondary-600">Note</th>
                    <th className="text-left px-4 py-2 font-medium text-secondary-600">Status</th>
                    <th className="text-right px-4 py-2 font-medium text-secondary-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {activationRequests.map((req) => (
                    <tr key={req.id} className="border-b border-secondary-100 hover:bg-secondary-50">
                      <td className="px-4 py-2.5 font-medium text-secondary-900">{req.organizationName}</td>
                      <td className="px-4 py-2.5 text-secondary-600">{req.requestedBy?.name}</td>
                      <td className="px-4 py-2.5 text-secondary-600 whitespace-nowrap">
                        {req.createdAt?.toDate().toLocaleDateString()}
                      </td>
                      <td className="px-4 py-2.5 text-secondary-500 max-w-[200px] truncate">
                        {req.note || '—'}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full capitalize ${getStatusBadge(req.status)}`}>
                          {req.status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {req.status === 'pending' && (
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleActivate(req)}
                              disabled={activatingId === req.id}
                            >
                              {activatingId === req.id ? 'Activating...' : 'Activate'}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => { setRejectTarget(req.id); setRejectTargetType('activation'); }}>
                              Reject
                            </Button>
                          </div>
                        )}
                        {req.status === 'rejected' && req.rejectionReason && (
                          <span className="text-xs text-secondary-500" title={req.rejectionReason}>
                            {req.rejectionReason.substring(0, 30)}{req.rejectionReason.length > 30 ? '...' : ''}
                          </span>
                        )}
                        {req.status === 'approved' && req.reviewedBy && (
                          <span className="text-xs text-secondary-500">
                            by {req.reviewedBy.name}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {activeTab === 'requests' && (
        <Card>
          <div className="p-4 border-b border-secondary-200 flex items-center justify-between">
            <h3 className="font-semibold text-secondary-900">All Credit Requests</h3>
            <Button size="sm" variant="outline" onClick={loadRequests}>
              Refresh
            </Button>
          </div>
          {requestsLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : requests.length === 0 ? (
            <div className="p-6 text-center text-secondary-500 text-sm">No credit requests yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-secondary-200 bg-secondary-50">
                    <th className="text-left px-4 py-2 font-medium text-secondary-600">Organization</th>
                    <th className="text-left px-4 py-2 font-medium text-secondary-600">Credits</th>
                    <th className="text-left px-4 py-2 font-medium text-secondary-600">Price</th>
                    <th className="text-left px-4 py-2 font-medium text-secondary-600">Requested By</th>
                    <th className="text-left px-4 py-2 font-medium text-secondary-600">Date</th>
                    <th className="text-left px-4 py-2 font-medium text-secondary-600">Status</th>
                    <th className="text-right px-4 py-2 font-medium text-secondary-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((req) => (
                    <tr key={req.id} className="border-b border-secondary-100 hover:bg-secondary-50">
                      <td className="px-4 py-2.5 font-medium text-secondary-900">{req.organizationName}</td>
                      <td className="px-4 py-2.5 text-secondary-700">{req.creditAmount.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-secondary-700">{req.currency} {req.price.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-secondary-600">{req.requestedBy?.name}</td>
                      <td className="px-4 py-2.5 text-secondary-600 whitespace-nowrap">
                        {req.createdAt?.toDate().toLocaleDateString()}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full capitalize ${getStatusBadge(req.status)}`}>
                          {req.status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {req.status === 'pending' && (
                          <div className="flex justify-end gap-2">
                            <Button size="sm" onClick={() => handleFulfill(req.id)}>
                              Fulfill
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => { setRejectTarget(req.id); setRejectTargetType('credit'); }}>
                              Reject
                            </Button>
                          </div>
                        )}
                        {req.status === 'rejected' && req.rejectionReason && (
                          <span className="text-xs text-secondary-500" title={req.rejectionReason}>
                            {req.rejectionReason.substring(0, 30)}{req.rejectionReason.length > 30 ? '...' : ''}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {activeTab === 'credits' && (
        <Card>
          <div className="p-4 border-b border-secondary-200 flex items-center justify-between">
            <h3 className="font-semibold text-secondary-900">Organization Credits Overview</h3>
            <Button size="sm" variant="outline" onClick={loadCredits}>
              Refresh
            </Button>
          </div>
          {creditsLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : orgCredits.length === 0 ? (
            <div className="p-6 text-center text-secondary-500 text-sm">No organizations have credits set up yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-secondary-200 bg-secondary-50">
                    <th className="text-left px-4 py-2 font-medium text-secondary-600">Organization</th>
                    <th className="text-right px-4 py-2 font-medium text-secondary-600">Available</th>
                    <th className="text-right px-4 py-2 font-medium text-secondary-600">Used (Lifetime)</th>
                    <th className="text-right px-4 py-2 font-medium text-secondary-600">Low Threshold</th>
                    <th className="text-left px-4 py-2 font-medium text-secondary-600">Last Top-Up</th>
                  </tr>
                </thead>
                <tbody>
                  {orgCredits.map((oc) => (
                    <tr key={oc.id} className="border-b border-secondary-100 hover:bg-secondary-50">
                      <td className="px-4 py-2.5 font-medium text-secondary-900">{getOrgName(oc.id)}</td>
                      <td className={`px-4 py-2.5 text-right font-medium ${
                        oc.messageCredits <= 0 ? 'text-red-600' :
                        oc.messageCredits <= oc.lowCreditThreshold ? 'text-yellow-600' : 'text-green-600'
                      }`}>
                        {oc.messageCredits.toLocaleString()}
                      </td>
                      <td className="px-4 py-2.5 text-right text-secondary-700">{oc.messagesUsed.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right text-secondary-500">{oc.lowCreditThreshold}</td>
                      <td className="px-4 py-2.5 text-secondary-600 whitespace-nowrap">
                        {oc.lastTopUpAt ? (
                          <>
                            {oc.lastTopUpAmount?.toLocaleString()} credits on{' '}
                            {oc.lastTopUpAt.toDate().toLocaleDateString()}
                          </>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {activeTab === 'topup' && (
        <Card>
          <div className="p-6">
            <h3 className="font-semibold text-secondary-900 mb-4">Manual Credit Top-Up</h3>
            <div className="max-w-md space-y-4">
              <div>
                <Label htmlFor="topUpOrg">Organization</Label>
                <select
                  id="topUpOrg"
                  value={topUpOrgId}
                  onChange={(e) => setTopUpOrgId(e.target.value)}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select an organization...</option>
                  {organizations.map((org) => {
                    const credits = orgCredits.find(oc => oc.id === org.id);
                    return (
                      <option key={org.id} value={org.id}>
                        {org.name}{credits ? ` (${credits.messageCredits} credits)` : ' (no credits yet)'}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div>
                <Label htmlFor="topUpAmount">Credit Amount</Label>
                <Input
                  id="topUpAmount"
                  type="number"
                  value={topUpAmount}
                  onChange={(e) => setTopUpAmount(e.target.value)}
                  placeholder="e.g., 100"
                  min="1"
                />
              </div>
              <Button onClick={handleTopUp} disabled={topUpLoading || !topUpOrgId || !topUpAmount}>
                {topUpLoading ? 'Adding...' : 'Add Credits'}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {activeTab === 'packages' && (
        <Card>
          <div className="p-4 border-b border-secondary-200 flex items-center justify-between">
            <h3 className="font-semibold text-secondary-900">Credit Packages</h3>
            <Button size="sm" onClick={() => setShowNewPackage(true)}>
              New Package
            </Button>
          </div>
          {packagesLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <div className="divide-y divide-secondary-100">
              {packages.map((pkg) => (
                <div key={pkg.id} className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-secondary-900">{pkg.name}</p>
                    <p className="text-sm text-secondary-500">
                      {pkg.creditAmount.toLocaleString()} credits &middot; {pkg.currency} {pkg.price.toLocaleString()}
                      &middot; Order: {pkg.order}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      pkg.isActive ? 'bg-green-50 text-green-700' : 'bg-secondary-100 text-secondary-500'
                    }`}>
                      {pkg.isActive ? 'Active' : 'Inactive'}
                    </span>
                    <Button size="sm" variant="outline" onClick={() => handleTogglePackage(pkg)}>
                      {pkg.isActive ? 'Deactivate' : 'Activate'}
                    </Button>
                  </div>
                </div>
              ))}
              {packages.length === 0 && (
                <div className="p-6 text-center text-secondary-500 text-sm">
                  No packages created yet. Create one to allow organizations to request credits.
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* New Package Dialog */}
      {showNewPackage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4 space-y-4">
            <h3 className="text-lg font-semibold text-secondary-900">Create Credit Package</h3>
            <div>
              <Label htmlFor="pkgName">Package Name</Label>
              <Input
                id="pkgName"
                value={newPkg.name}
                onChange={(e) => setNewPkg(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Starter Pack"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="pkgCredits">Credits</Label>
                <Input
                  id="pkgCredits"
                  type="number"
                  value={newPkg.creditAmount}
                  onChange={(e) => setNewPkg(prev => ({ ...prev, creditAmount: e.target.value }))}
                  placeholder="100"
                  min="1"
                />
              </div>
              <div>
                <Label htmlFor="pkgPrice">Price</Label>
                <Input
                  id="pkgPrice"
                  type="number"
                  value={newPkg.price}
                  onChange={(e) => setNewPkg(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="10.00"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="pkgCurrency">Currency</Label>
                <Input
                  id="pkgCurrency"
                  value={newPkg.currency}
                  onChange={(e) => setNewPkg(prev => ({ ...prev, currency: e.target.value }))}
                  placeholder="USD"
                />
              </div>
              <div>
                <Label htmlFor="pkgOrder">Display Order</Label>
                <Input
                  id="pkgOrder"
                  type="number"
                  value={newPkg.order}
                  onChange={(e) => setNewPkg(prev => ({ ...prev, order: e.target.value }))}
                  placeholder="0"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowNewPackage(false)}>Cancel</Button>
              <Button onClick={handleCreatePackage}>Create Package</Button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Dialog */}
      {rejectTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4 space-y-4">
            <h3 className="text-lg font-semibold text-secondary-900">
              Reject {rejectTargetType === 'activation' ? 'Activation' : 'Credit'} Request
            </h3>
            <div>
              <Label htmlFor="rejectReason">Reason (optional)</Label>
              <textarea
                id="rejectReason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Reason for rejection..."
                rows={3}
                className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setRejectTarget(null); setRejectTargetType('credit'); setRejectReason(''); }}>
                Cancel
              </Button>
              <Button onClick={handleReject}>Reject</Button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium ${
          toast.type === 'success' ? 'bg-green-600' :
          toast.type === 'error' ? 'bg-red-600' : 'bg-blue-600'
        }`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default CreditManagement;
